import { apiService } from "@/services";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { handleApiError } from "@/utils";
import { InviteMemberParams, MembersResponse, UpdateMemberParams } from "@/types";
import { DEFAULT_STALE_TIME } from "@/constants";

export const useGetMembers = () =>
  useQuery({
    queryKey: ['members'],
    queryFn: () => apiService.get<MembersResponse, unknown>('/members'),
    staleTime: DEFAULT_STALE_TIME,
  });

export const useInviteMember = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, InviteMemberParams>({
    mutationFn: (data) => apiService.post('/members/invite', data),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['members'] });
    },
    onError: (error) => handleApiError(error),
  });
};

export const useUpdateMember = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, { id: number; data: UpdateMemberParams }>({
    mutationFn: ({ id, data }) => apiService.patch(`/members/${id}`, data),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['members'] });
    },
    onError: (error) => handleApiError(error),
  });
};

export const useRemoveMember = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, number>({
    mutationFn: (id) => apiService.delete(`/members/${id}`),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['members'] });
    },
    onError: (error) => handleApiError(error),
  });
};

export const useCancelInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, number>({
    mutationFn: (id) => apiService.delete(`/members/invitations/${id}`),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['members'] });
    },
    onError: (error) => handleApiError(error),
  });
};

export const useResendInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation<unknown, unknown, number>({
    mutationFn: (id) => apiService.post(`/members/invitations/${id}/resend`),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['members'] });
    },
    onError: (error) => handleApiError(error),
  });
};
